/* Maison Sabelle product page: option pills, variant state, engraving and gift fields, add to bag through
   Horizon's cart drawer (the same standard event its own product form sends), the gallery counter, the image
   viewer, the size guide and the phone add-to-bag bar. */

let events = null;
const loadEvents = () => {
  if (!events) events = import('@shopify/events').catch(() => null);
  return events;
};

const money = (cents, currency) => {
  const whole = cents % 100 === 0;
  return new Intl.NumberFormat('en-GB', {
    style: 'currency',
    currency: currency || 'GBP',
    minimumFractionDigits: whole ? 0 : 2,
    maximumFractionDigits: whole ? 0 : 2,
  }).format(cents / 100);
};

function init(root) {
  if (root.dataset.sabelleProductReady) return;
  root.dataset.sabelleProductReady = '1';

  const section = root.closest('.shopify-section') || document;
  const json = section.querySelector('[data-spd-variants]');
  const variants = json ? JSON.parse(json.textContent) : [];
  const currency = root.dataset.currency;
  const form = root.querySelector('[data-form]');
  const idInput = root.querySelector('[data-variant-id]');
  const fieldsets = [...root.querySelectorAll('[data-option]')];
  const priceEls = [root.querySelector('[data-price]'), root.querySelector('[data-add-price]'), root.querySelector('[data-bar-price]')].filter(Boolean);
  const addBtn = root.querySelector('[data-add]');
  const addLabel = root.querySelector('[data-add-label]');
  const barBtn = root.querySelector('[data-bar-btn]');
  const errorEl = root.querySelector('[data-error]');
  const live = root.querySelector('[data-live]');
  const track = root.querySelector('[data-track]');

  const selected = () => fieldsets.map((fs) => fs.querySelector('input:checked')?.value);
  const find = (opts) => variants.find((v) => v.options.every((o, i) => o === opts[i]));

  /* Strike through values that can't be bought with the other choices as they stand. */
  const markAvailability = (opts) => {
    fieldsets.forEach((fs, i) => {
      fs.querySelectorAll('.spd__pill').forEach((pill) => {
        const val = pill.querySelector('input').value;
        const test = opts.slice();
        test[i] = val;
        const v = find(test);
        pill.classList.toggle('is-out', !v || !v.available);
      });
    });
  };

  const showMedia = (mediaId) => {
    if (!track) return;
    track.querySelectorAll('[data-variant-media]').forEach((li) => {
      li.hidden = String(li.dataset.mediaId) !== String(mediaId);
    });
    const lead = track.querySelector(`[data-media-id="${mediaId}"]`);
    if (lead && lead !== track.firstElementChild) track.prepend(lead);
    track.scrollTo({ left: 0, behavior: 'smooth' });
    updateCount();
  };

  const apply = (v, opts) => {
    fieldsets.forEach((fs, i) => {
      const out = fs.querySelector('[data-option-value]');
      if (out) out.textContent = opts[i];
    });
    markAvailability(opts);
    const ok = Boolean(v && v.available);
    if (v) {
      idInput.value = v.id;
      priceEls.forEach((el) => { el.textContent = money(v.price, currency); });
      if (v.media) showMedia(v.media);
      const url = new URL(location.href);
      url.searchParams.set('variant', v.id);
      history.replaceState(history.state, '', url);
    }
    addBtn.disabled = !ok;
    addLabel.textContent = v ? (ok ? 'Add to bag' : 'Sold out') : 'Unavailable';
    if (barBtn) {
      barBtn.disabled = !ok;
      barBtn.textContent = addLabel.textContent;
    }
  };

  fieldsets.forEach((fs) => fs.addEventListener('change', () => {
    const opts = selected();
    apply(find(opts), opts);
  }));
  if (fieldsets.length) {
    const opts = selected();
    const v = find(opts);
    markAvailability(opts);
    if (v && v.media && track && track.firstElementChild?.dataset.mediaId !== String(v.media)) showMedia(v.media);
  }

  /* Engraving and gift: fields only travel with the order when their box is ticked. */
  root.querySelectorAll('[data-extra]').forEach((box) => {
    const toggle = box.querySelector('[data-extra-toggle]');
    const body = box.querySelector('.spd__extra-body');
    const fields = body.querySelectorAll('input, textarea');
    toggle.addEventListener('change', () => {
      body.hidden = !toggle.checked;
      fields.forEach((f) => { f.disabled = !toggle.checked; });
      if (toggle.checked) body.querySelector('input:not([type=hidden]), textarea')?.focus();
    });
  });
  const engrave = root.querySelector('[data-engrave]');
  const engraveCount = root.querySelector('[data-engrave-count]');
  engrave?.addEventListener('input', () => { engraveCount.textContent = engrave.value.length; });

  /* Add to bag. */
  form.addEventListener('submit', async (e) => {
    e.preventDefault();
    if (addBtn.disabled || addBtn.classList.contains('is-busy')) return;
    errorEl.hidden = true;
    addBtn.classList.add('is-busy');

    const data = new FormData(form);
    const ids = [...document.querySelectorAll('cart-items-component')].map((el) => el.dataset.sectionId).filter(Boolean);
    if (ids.length) data.append('sections', ids.join(','));

    const mod = await loadEvents();
    const Ev = mod?.CartLinesUpdateEvent;
    const deferred = Ev?.createPromise?.();
    if (Ev && deferred) {
      form.dispatchEvent(new Ev({
        action: 'add',
        context: 'product',
        lines: [{ merchandiseId: String(data.get('id')), quantity: 1 }],
        promise: deferred.promise,
      }));
    }

    const routes = window.Theme?.routes || {};
    try {
      const res = await fetch(routes.cart_add_url || '/cart/add', {
        method: 'POST',
        headers: { Accept: 'application/javascript', 'X-Requested-With': 'XMLHttpRequest' },
        body: data,
      }).then((r) => r.json());
      const cart = await fetch(`${routes.cart_url || '/cart'}.json`).then((r) => r.json());
      const failed = Boolean(res.status);
      deferred?.resolve({
        cart: Ev.createCartFromAjaxResponse(cart),
        detail: {
          items: cart.items,
          source: 'sabelle-product',
          sourceId: form.id,
          itemCount: 1,
          productId: root.dataset.productId,
          sections: failed ? undefined : res.sections,
          didError: failed,
        },
      });
      if (failed) throw new Error(res.description || res.message || 'That could not be added.');
      addBtn.classList.add('is-added');
      addLabel.textContent = 'Added';
      live.textContent = 'Added to your bag';
      setTimeout(() => {
        addBtn.classList.remove('is-added');
        addLabel.textContent = 'Add to bag';
      }, 2200);
    } catch (err) {
      deferred?.reject?.(err);
      errorEl.textContent = err.message;
      errorEl.hidden = false;
    } finally {
      addBtn.classList.remove('is-busy');
    }
  });

  /* Gallery counter on phones. */
  const countNow = root.querySelector('[data-count-now]');
  const countAll = root.querySelector('[data-count-all]');
  /* Desktop grid: the lead image spans both columns; if the rest leave an odd one out, it spans too. */
  function layout() {
    if (!track) return;
    const slides = [...track.children].filter((li) => !li.hidden);
    slides.forEach((li) => li.classList.remove('is-wide'));
    if (slides.length > 1 && (slides.length - 1) % 2 === 1) slides[slides.length - 1].classList.add('is-wide');
  }
  function updateCount() {
    layout();
    if (!track || !countNow) return;
    const slides = [...track.children].filter((li) => !li.hidden);
    countAll.textContent = slides.length;
    const x = track.scrollLeft;
    let i = 0;
    slides.forEach((li, k) => { if (li.offsetLeft - track.offsetLeft - 16 <= x + 4) i = k; });
    countNow.textContent = i + 1;
  }
  track?.addEventListener('scroll', () => requestAnimationFrame(updateCount), { passive: true });
  updateCount();

  /* Image viewer. */
  const view = section.querySelector('[data-view]');
  const viewImg = view?.querySelector('[data-view-img]');
  root.querySelectorAll('[data-zoom]').forEach((btn) => btn.addEventListener('click', () => {
    if (!view?.showModal) return;
    viewImg.src = btn.dataset.zoom;
    viewImg.alt = btn.querySelector('img')?.alt || '';
    view.showModal();
  }));
  view?.addEventListener('click', () => view.close());

  /* Size guide. */
  const guide = section.querySelector('[data-guide]');
  root.querySelectorAll('[data-guide-open]').forEach((b) => b.addEventListener('click', () => guide?.showModal?.()));
  guide?.querySelector('[data-guide-close]')?.addEventListener('click', () => guide.close());
  guide?.addEventListener('click', (e) => { if (e.target === guide) guide.close(); });

  /* Phone bar: shows once the main button has scrolled away. */
  const bar = root.querySelector('[data-bar]');
  if (bar && 'IntersectionObserver' in window) {
    const io = new IntersectionObserver(([entry]) => {
      const below = entry.boundingClientRect.top < 0;
      const on = !entry.isIntersecting && below;
      bar.classList.toggle('is-on', on);
      bar.setAttribute('aria-hidden', String(!on));
      if (barBtn) barBtn.tabIndex = on ? 0 : -1;
    });
    io.observe(addBtn);
  }

  loadEvents();
}

const boot = () => document.querySelectorAll('[data-sabelle-product]').forEach(init);
if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
else boot();
document.addEventListener('shopify:section:load', boot);
